import {useQuery} from '@tanstack/react-query';
import {useNavigate,useSearchParams} from 'react-router-dom';
import {AppFrame,Button,Card,PageHeader} from '@pocketbuy/outdoor-design-system';
import {inventoryApi} from '../../api';
import {ImeiCameraScanner} from '../../components/ImeiCameraScanner';
import {RefreshIcon} from '../../components/RefreshIcon';
import {LocalPhone} from '../../components/LocalPhone';
import {SalesDeadline} from '../sales/SalesDeadline';
import {pickupTime} from '../pickup/pickupPresentation';
import {inventoryPhoneStatus,phoneLocation,phoneNextStep,phoneRecordLabel,phoneRecordPath,phoneWithAgent,type InventoryPhone} from './inventoryPhone';

export function FoundPhone({phone,onOpen}:{phone:InventoryPhone;onOpen:(path:string)=>void}){
  const next=phoneNextStep(phone);
  return <div className="pb-stack">
    <Card className="pb-stack">
      <h2 className="op-section-title">{phone.skuName}</h2>
      <span className="op-status">{inventoryPhoneStatus(phone.status)}</span>
      <div className="op-pickup-device">
        {phone.imeis.map((imei,index)=><div className="op-key-row" key={imei}><span>IMEI {index+1}</span><b>{imei}</b></div>)}
      </div>
      <div className="pb-stack">
        <div><small className="pb-muted">{phoneWithAgent(phone.status)?'Current agent':'Current location'}</small><div><strong>{phoneLocation(phone)}</strong></div></div>
        {phoneWithAgent(phone.status)&&<div>{phone.custodianAccount&&<div>{phone.custodianAccount}</div>}<LocalPhone value={phone.custodianPhone}/><div><SalesDeadline value={phone.salesDueAt??undefined} timeZone={phone.timeZone}/></div></div>}
        {phone.status!=='STOCK_IN_TRANSIT'&&phoneLocation(phone)!==phone.stockPointName&&<div><small className="pb-muted">Stock point</small><div>{phone.stockPointName}</div></div>}
      </div>
      {phone.updatedAt&&<small className="pb-muted">Updated {pickupTime(phone.updatedAt,phone.timeZone)}</small>}
    </Card>
    {next&&<p className="op-notice">{next}</p>}
    <section className="pb-stack"><h2 className="op-section-title">Phone history</h2><Card className="op-list">
      {phone.records.map(record=>{
        const path=phoneRecordPath(record);
        const content=<><span className="op-list-copy"><strong>{phoneRecordLabel(record)}</strong>
          {record.reference&&<small>{record.reference}</small>}
          {record.personName&&<small>{record.type==='REALLOCATION'?`${record.personName} → ${record.otherPersonName??'Agent not recorded'}`:record.type==='STOCK_IN'?`Recorded by ${record.personName}`:record.type==='CUSTOMER_RETURN'?`Handled by ${record.personName}`:`Agent: ${record.personName}`}</small>}
          {record.type==='RETURN'&&record.otherPersonName&&<small>Received by {record.otherPersonName}</small>}
          {record.stockPointName&&<small>{record.stockPointName}</small>}
          <small>{pickupTime(record.occurredAt??undefined,record.timeZone)}</small>
        </span>{path&&<span aria-hidden="true">›</span>}</>;
        return path?<button key={`${record.type}-${record.id}`} className="op-list-item" onClick={()=>onOpen(path)}>{content}</button>:<div key={`${record.type}-${record.id}`} className="op-list-item">{content}</div>;
      })}
      {phone.records.length===0&&<p className="op-empty">No movement records are available.</p>}
    </Card></section>
  </div>;
}

export function FindPhonePage(){
  const nav=useNavigate();const [params,setParams]=useSearchParams();
  const imei=params.get('imei')??'';const point=params.get('stockPointId');
  const valid=/^\d{15}$/.test(imei);
  const phone=useQuery({queryKey:['inventory-phone',imei],queryFn:()=>inventoryApi.findPhone(imei),enabled:valid,staleTime:0,refetchOnWindowFocus:false,retry:1});
  const update=(value:string)=>setParams(current=>{const next=new URLSearchParams(current);if(value)next.set('imei',value);else next.delete('imei');return next},{replace:true});
  const returnTo=`/inventory/find-phone?${params.toString()}`;
  return <AppFrame className="op-browse-page">
    <PageHeader title="Find phone" onBack={()=>nav(point?`/inventory?stockPointId=${encodeURIComponent(point)}`:'/inventory')}/>
    <div className="pb-stack op-browse-content">
      <p className="op-page-sub">Search all your assigned stock points, including phones with agents.</p>
      <Card><ImeiCameraScanner label="Phone IMEI" value={imei} onChange={update} validation="format"/></Card>
      {valid&&<div className="op-browse-toolbar"><strong>Phone details</strong><RefreshIcon busy={phone.isFetching} onClick={()=>void phone.refetch()} label="Refresh phone details"/></div>}
      {valid&&phone.isPending&&<Card><p role="status">Finding phone…</p></Card>}
      {valid&&phone.isError&&<Card className="pb-stack"><p role="alert" className="op-error">Phone details could not be loaded. Check your connection and try again.</p><Button className="secondary" onClick={()=>void phone.refetch()}>Try again</Button></Card>}
      {valid&&!phone.isError&&phone.data===null&&<Card><div className="op-empty"><b>Phone not found</b><small>No matching phone was found at your assigned stock points. Check the complete IMEI or scan another IMEI on the same phone.</small></div></Card>}
      {valid&&!phone.isError&&phone.data&&<FoundPhone phone={phone.data} onOpen={path=>nav(path,{state:{returnTo}})}/>}
    </div>
  </AppFrame>;
}
